import React from 'react'
import { useInView } from 'react-intersection-observer'
import SectionBackground from './SectionBackground'
// React Icons - Technology specific icons
import { 
  FaReact, FaNodeJs, FaPython, FaDocker, FaAws, FaGitAlt, FaFigma, FaJava
} from 'react-icons/fa'
import { 
  SiNextdotjs, SiTypescript, SiTailwindcss, SiRedux, SiExpress, 
  SiDjango, SiPostgresql, SiMongodb, SiMysql, SiRedis, SiFirebase,
  SiKubernetes, SiTerraform, SiJest, SiPostman
} from 'react-icons/si'
import { VscCode } from 'react-icons/vsc'
// Lucide icons for category headers
import { Palette, Server, Database, Cloud, Wrench } from 'lucide-react'

const About = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const skills = [
    {
      category: 'Frontend',
      icon: Palette,
      items: [
        { name: 'React', icon: FaReact },
        { name: 'Next.js', icon: SiNextdotjs },
        { name: 'TypeScript', icon: SiTypescript },
        { name: 'Tailwind CSS', icon: SiTailwindcss },
        { name: 'Redux', icon: SiRedux }
      ]
    },
    {
      category: 'Backend',
      icon: Server,
      items: [
        { name: 'Node.js', icon: FaNodeJs },
        { name: 'Express', icon: SiExpress },
        { name: 'Python', icon: FaPython },
        { name: 'Django', icon: SiDjango },
        { name: 'Java', icon: FaJava }
      ]
    },
    {
      category: 'Database',
      icon: Database,
      items: [
        { name: 'PostgreSQL', icon: SiPostgresql },
        { name: 'MongoDB', icon: SiMongodb },
        { name: 'MySQL', icon: SiMysql },
        { name: 'Redis', icon: SiRedis },
        { name: 'Firebase', icon: SiFirebase }
      ]
    },
    {
      category: 'Cloud & DevOps',
      icon: Cloud,
      items: [
        { name: 'AWS', icon: FaAws },
        { name: 'Docker', icon: FaDocker },
        { name: 'Kubernetes', icon: SiKubernetes },
        { name: 'CI/CD', icon: FaGitAlt },
        { name: 'Terraform', icon: SiTerraform }
      ]
    },
    {
      category: 'Tools',
      icon: Wrench,
      items: [
        { name: 'Git', icon: FaGitAlt },
        { name: 'Jest', icon: SiJest },
        { name: 'Figma', icon: FaFigma },
        { name: 'VS Code', icon: VscCode },
        { name: 'Postman', icon: SiPostman }
      ]
    }
  ]

  return (
    <section
      id="about"
      ref={ref}
      className="relative py-20 px-4 sm:px-6 lg:px-8"
    >
      <SectionBackground variant="about" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div
          className={`transition-all duration-1000 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-dark dark:text-navy-lightest mb-4">
              About Me
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-navy-medium to-navy-deep dark:from-github-border dark:to-github-border mx-auto"></div>
          </div>

          {/* Content Grid */}
          <div className="grid lg:grid-cols-3 gap-12 items-start mb-12">
            {/* Profile Image */}
            <div className="lg:col-span-1 flex justify-center">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-navy-medium to-navy-deep rounded-full blur opacity-75"></div>
                <img
                  src="/about.png"
                  alt="Profile"
                  className="relative w-64 h-64 rounded-full object-cover border-4 border-white dark:border-navy-dark shadow-xl"
                />
              </div>
            </div>

            {/* Bio */}
            <div className="lg:col-span-2">
              <h3 className="text-2xl font-bold text-navy-dark dark:text-navy-lightest mb-4">
                Hello! I'm Shehan Chamika, a passionate software engineering student.
              </h3>
              <div className="space-y-4 text-navy-base dark:text-navy-soft leading-relaxed">
                <p>
                  I'm currently a 1st year Software Engineering student at Java Institute for Advanced Technology,
                  specializing in building modern web applications and scalable digital solutions. My journey
                  started with a curiosity for how things work on the web, and it evolved into
                  a passion I'm truly dedicated to.
                </p>
                <p>
                  I thrive on challenges and love learning new technologies to deliver products that make
                  a difference. Whether it's architecting robust backend systems or crafting pixel-perfect
                  user interfaces, I bring dedication and enthusiasm to every project.
                </p>
                <p>
                  When I'm not coding, you'll find me contributing to open-source projects, creating content
                  on YouTube (@codemaster-l5e), or exploring the latest in tech and design.
                </p>
              </div>
            </div>
          </div>

          {/* Skills Section - Full Width */}
          <div className="w-full">
            <h3 className="text-3xl font-bold text-navy-dark dark:text-navy-lightest mb-8 text-center">
              Technical Skills
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              {skills.map((skillGroup, idx) => {
                const CategoryIcon = skillGroup.icon
                return (
                  <div
                    key={skillGroup.category}
                    className={`transition-all duration-700 delay-${idx * 100} ${
                      inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
                  >
                    <div className="bg-gradient-to-br from-navy-pale to-navy-soft/30 dark:from-navy-deep dark:to-navy-base rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-navy-soft dark:border-navy-base group hover:border-accent h-full">
                      {/* Category Header */}
                      <div className="flex items-center gap-3 mb-6 pb-4 border-b border-navy-soft dark:border-navy-base">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-navy-medium to-navy-deep flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                          <CategoryIcon className="w-5 h-5 text-white" strokeWidth={2.5} />
                        </div>
                        <h4 className="text-base font-bold text-navy-dark dark:text-navy-lightest">
                          {skillGroup.category}
                        </h4>
                      </div>
                      
                      {/* Skills Icons Grid - Icons Only */}
                      <div className="grid grid-cols-3 gap-3">
                        {skillGroup.items.map((skill) => {
                          const SkillIcon = skill.icon
                          return (
                            <div
                              key={skill.name}
                              className="group/skill relative flex justify-center"
                            >
                              <div className="w-14 h-14 rounded-xl bg-white dark:bg-navy-dark border-2 border-navy-soft dark:border-navy-base hover:border-accent dark:hover:border-accent flex items-center justify-center transition-all duration-300 hover:scale-125 hover:shadow-xl cursor-pointer hover:z-10 relative">
                                <SkillIcon className="w-7 h-7 text-navy-base dark:text-navy-soft group-hover/skill:text-accent dark:group-hover/skill:text-accent transition-colors" />
                              </div>
                              {/* Tooltip - Only appears on hover */}
                              <div className="absolute -top-10 left-1/2 transform -translate-x-1/2 bg-navy-dark dark:bg-navy-deep text-white text-xs px-3 py-1.5 rounded-lg opacity-0 group-hover/skill:opacity-100 transition-all duration-200 pointer-events-none whitespace-nowrap z-20 shadow-xl border border-accent/30">
                                {skill.name}
                                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-navy-dark dark:bg-navy-deep rotate-45 border-r border-b border-accent/30"></div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default About